import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";
import { ChannelMemberResponse } from "stream-chat";

import { getEventMembers } from "../../../store/selectors";
import GeneralDiscussionContainer from "../../../../Discussions/containers/GeneralDiscussionContainer/GeneralDiscussionContainer";
import { selectors } from "../../../store";
import { updateEventSettings } from "../../../store/actions";
import { selectors as userSelectors } from "../../../../Auth";
import EventAuthInner from "../EventAuthDialog/EventAuthInner";
import { DiscussionsContainer } from "../../../../Discussions/containers/DiscussionsContainer";

import { EventSideDialog } from "shared/components/Event/EventSideDialog";
import {
  getChannels,
  getSelectedChannelDetails,
  getShowChatList,
  getStreamChannels,
} from "containers/Discussions/store/selectors";
import { useGetStream } from "shared/hooks/GetStreamHook";
import { customizeGetstreamChannel, getChannelName } from "shared/utils/getStream";
import { clearSelectedChannel, showChatList } from "containers/Discussions/store/actions";
import { EventSideDialogHeaderTypes } from "shared/components/Event/EventSideDialog/EventSideDialogHeader";
import { DiscussionTab } from "shared/components/Event/EventSideDialog/EventSideDialogHeader/EventSideDialogDiscussionTabs";
import { CanView, useUnreadCount } from "shared";
import { getMember } from "containers/Auth/store/selectors";

export interface EventDiscussionsProps {
  open: boolean;
  onClose: () => void;
  onLogin: () => void;
  onSignup: () => void;
  onAuthSuccess: () => void;
  onReplyGreeterCustomCh: () => void;
  openRestrictionAlert: () => void;
}

const EventDiscussions: React.FC<EventDiscussionsProps> = props => {
  const { open, onClose, onLogin, onSignup, onAuthSuccess, onReplyGreeterCustomCh, openRestrictionAlert } = props;

  const dispatch = useDispatch();

  const event = useSelector(selectors.getEvent());
  const { selectedTab } = useSelector(selectors.getEventSettings());
  const isAuthenticated = useSelector(userSelectors.authentificated());
  const selectedChannel = useSelector(getSelectedChannelDetails());
  const streamChannels = useSelector(getStreamChannels());
  const eventMembers = useSelector(getEventMembers());
  const chatListStatus = useSelector(getShowChatList());
  const currentMember = useSelector(getMember());
  const userChannels = useSelector(getChannels());

  const currentGetStreamChannel = useMemo(() => {
    return streamChannels?.find(channel => channel.id === selectedChannel?.id);
  }, [streamChannels, selectedChannel]);
  let dialogTitle = "";
  let dialogImage: string | undefined;
  let secondMember: ChannelMemberResponse | null = null;
  if (currentGetStreamChannel) {
    const { getChannelImage, interlocutor } = customizeGetstreamChannel(currentGetStreamChannel, selectedChannel);
    const channelName = getChannelName(selectedChannel, currentMember);
    dialogImage = getChannelImage();
    dialogTitle = channelName;
    if (interlocutor) {
      secondMember = interlocutor;
    }
  }

  const handleBackDialog = useCallback(() => {
    if (chatListStatus) return;
    return () => {
      dispatch(showChatList(true));
      dispatch(clearSelectedChannel());
    };
  }, [chatListStatus, dispatch]);

  const setSelectedTab = useCallback(
    (selectedTab: DiscussionTab) => {
      dispatch(updateEventSettings({ selectedTab }));
    },
    [dispatch],
  );

  const handleCloseDialog = () => {
    if (!chatListStatus) {
      dispatch(showChatList(true));
      dispatch(clearSelectedChannel());
    }
    onClose();
  };

  useEffect(() => {
    if (selectedChannel && userChannels && !userChannels.find(channel => channel.id === selectedChannel.id)) {
      dispatch(clearSelectedChannel());
      dispatch(showChatList(true));
    }
  }, [selectedChannel, userChannels, dispatch]);

  const [color, customName] = useMemo(() => {
    let color = selectedChannel?.color;
    const participant = eventMembers.find(({ memberId }) => String(memberId) === secondMember?.user?.id);
    if (participant?.color) {
      color = participant.color;
    }
    return [color, participant?.guestName || secondMember?.user?.name];
  }, [eventMembers, secondMember, selectedChannel]);

  if (customName) {
    dialogTitle = customName;
  }

  const dialogClassName = classnames("eventDiscussions", {
    "eventDiscussions-hidden": !open,
    "eventDiscussions-chat": !!dialogTitle,
  });

  dialogTitle = dialogTitle || "Chat";

  const chatClient = useGetStream();

  useUnreadCount(chatClient);

  const discussionContainer = useMemo(() => {
    if (!chatClient) {
      return null;
    }
    return (
      <>
        <CanView condition={selectedTab === DiscussionTab.personal} hideWithDisplay>
          {!!event?.greeters.length || isAuthenticated ? (
            <DiscussionsContainer
              theme="stream"
              isPartialView
              isEventDiscussions
              onLogin={onLogin}
              onSignup={onSignup}
              onReplyGreeterCustomCh={onReplyGreeterCustomCh}
              openRestrictionAlert={openRestrictionAlert}
            />
          ) : (
            <EventAuthInner
              className="embedded"
              onClose={onClose}
              onAuthSuccess={onAuthSuccess}
              message="Please log in or sign up to chat and interact with other online participants."
            />
          )}
        </CanView>
        <CanView condition={selectedTab === DiscussionTab.general} hideWithDisplay>
          <GeneralDiscussionContainer openRestrictionAlert={openRestrictionAlert} />
        </CanView>
      </>
    );
  }, [
    chatClient,
    event,
    isAuthenticated,
    onAuthSuccess,
    onClose,
    onLogin,
    onSignup,
    selectedTab,
    onReplyGreeterCustomCh,
    openRestrictionAlert,
  ]);

  return (
    <EventSideDialog
      showImage={!!selectedChannel}
      image={dialogImage}
      onClose={handleCloseDialog}
      open={open}
      title={dialogTitle}
      className={dialogClassName}
      imageColor={color}
      onBack={handleBackDialog()}
      headerType={
        currentGetStreamChannel && !chatListStatus
          ? EventSideDialogHeaderTypes.discussions
          : EventSideDialogHeaderTypes.discussion_tabs
      }
      selectedTab={selectedTab}
      onChangeTab={setSelectedTab}
    >
      {discussionContainer}
    </EventSideDialog>
  );
};

export default EventDiscussions;
